import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import materialityConfig from 'constants/materialityConfig';
import useErrorMessage from 'hooks/useErrorMessage';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import topicRefinement from 'mocks/topicRefinement';
import { useAppSelector } from 'store/hooks/useApp';
import { selectStakeholderVcTab } from 'store/slices/general/selectors';
import { setStakeholderVcTab } from 'store/slices/general/slice';
import { useGetMaterialityStakeholdersListStatusQuery, useGetSectionsMAQuery, useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import { useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityAssessmentTopicRefinementView from './MaterialityAssessmentTopicRefinementView';

import type { SectionMAStatusValue } from 'store/slices/layoutsMA/interfaces/ISectionMAType';
import type ITopicsItem from './interfaces/ITopicsItem';

function MaterialityAssessmentTopicRefinement() {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const dispatch = useDispatch();
  const [topics, setTopics] = useState<ITopicsItem[]>([]);
  const stakeholderVcTab = useAppSelector(selectStakeholderVcTab);

  const [updateSection] = useUpdateSectionMAMutation();

  const { data: sectionsData, error: fetchError } = useGetMaterialityStakeholdersListStatusQuery({
    maId: recordId,
    type: SECTION_TYPE.TOPIC_IDENTIFICATION_1_VCM,
    vcmId: stakeholderVcTab || 0,
  }, {
    skip: stakeholderVcTab === null,
  });

  const { data: sectionsDataMain } = useGetSectionsMAQuery({
    maId: recordId,
  });
  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: userData } = useUserMeQuery();
  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const errorMessage = useErrorMessage(fetchError);

  useEffect(() => {
    if (singleMA && singleMA?.data?.valueChainMaps && singleMA?.data?.valueChainMaps?.length > 0
      && stakeholderVcTab === null) {
      dispatch(setStakeholderVcTab(singleMA.data.valueChainMaps[0].id));
    }
  }, [singleMA, dispatch, stakeholderVcTab]);

  useEffect(() => {
    if (errorMessage && errorMessage !== '') {
      toast.error(i18n.t('notifications.errorHandlerTextMessage', { message: errorMessage }));
    }
  }, [errorMessage]);

  useEffect(() => {
    if (sectionsData && sectionsData.data) {
      const updateList = topicRefinement.map((topic) => {
        const section = sectionsData.data.subSections.find((sub) => sub.type === topic.type);
        if (section) {
          return {
            ...topic, active: section.isActive, configure: section.status,
          };
        }
        return topic;
      }) as ITopicsItem[];
      setTopics(updateList);
      const checkStatus = updateList.every((item) => item.configure === STATUS_SECTIONS.COMPLETED);
      if (checkStatus) {
        updateSection({
          maId: recordId,
          type: SECTION_TYPE.TOPIC_IDENTIFICATION_1_VCM,
          valueChainMapId: Number(stakeholderVcTab),
          data: {
            isActive: true,
            status: STATUS_SECTIONS.IN_PROGRESS,
          },
        });
      }
    }
  }, [sectionsData, updateSection, recordId, stakeholderVcTab]);

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentInitiate.replace(':recordId', recordId));
  };

  const checkAllVCStatuses = (): SectionMAStatusValue => {
    let hasCompleted: SectionMAStatusValue = STATUS_SECTIONS.IN_PROGRESS;
    const section = sectionsDataMain?.data?.items.find((item) => item.type === SECTION_TYPE.TOPIC_IDENTIFICATION_1);

    if (section?.subSections && section.subSections.length > 0) {
      const allCompleted = section.subSections.every((subSection) => subSection.status === STATUS_SECTIONS.COMPLETED);

      if (allCompleted) {
        hasCompleted = STATUS_SECTIONS.COMPLETED;
      }
    }

    return hasCompleted;
  };

  const onHandleNext = () => {
    const currentStatus = checkAllVCStatuses();
    updateSection({
      maId: recordId,
      type: SECTION_TYPE.TOPIC_IDENTIFICATION_1,
      data: {
        isActive: true,
        status: currentStatus,
      },
    });
    navigate(AppRoutes.materialityAssessmentTopicRefinement.replace(':recordId', recordId));
  };

  const onPrevious = () => {
    navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
  };

  const handleClickVcTab = (vcId: number) => {
    dispatch(setStakeholderVcTab(vcId));
  };

  const onChangeStatus = (id: number) => {
    if (isViewOnly) {
      return;
    }
    const topic = topics.find((item) => item.id === id);
    if (topic) {
      updateSection({
        maId: recordId,
        type: topic.type,
        valueChainMapId: stakeholderVcTab || 0,
        data: {
          isActive: !topic.active,
        },
      }).then(() => {
        toast.success(i18n.t('notifications.topicStatusUpdated'));
      }).catch(() => {
        toast.error(i18n.t('notifications.errorTopicStatusUpdate'));
      });
    }
    setTopics((prev) => prev.map((item) => {
      if (item.id === id) {
        return { ...item, active: !item.active };
      }
      return item;
    }));
  };

  const onConfigure = (id: number) => {
    const configItem = materialityConfig.find((item) => item.id === id);
    if (configItem) {
      navigate(configItem.url.replace(':recordId', recordId).replace(':vcmId', stakeholderVcTab?.toString() || ''));
    }
  };

  return (
    <MaterialityAssessmentTopicRefinementView
      goBack={goBack}
      onHandleNext={onHandleNext}
      onChangeStatus={onChangeStatus}
      topics={topics}
      onConfigure={onConfigure}
      onPrevious={onPrevious}
      mainVcTabs={singleMA?.data?.valueChainMaps?.map(
        (itemVC) => ({ value: itemVC.name, id: itemVC.id, name: itemVC.name }),
      ) || []}
      handleClickVcTab={handleClickVcTab}
      activeVcTab={stakeholderVcTab || 0}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityAssessmentTopicRefinement;
